import {tokens} from '@packages/utilities/styled-tokens';
import type {RequireAtLeastOne} from 'type-fest';

import type {ThemeTokens} from '@packages/utilities/styled-tokens';

/**
 * # Breakpoints
 *
 * A system for emotion styled that makes it easier to declaratively render
 * variant styles for specific breakpoints (preset media queries.)
 *
 * @example
 * ```
 * <Stack direction={{'@bp1': 'row', '@bp2': 'column}} />
 * ```
 */

/**
 * A custom Flow helper to create a responsive variant prop to be passed
 * into a styled component from emotion styled.
 *
 * ## Usage
 *
 * ```jsx
 * import {
 *   emotionStyled,
 *   type BreakpointPropFor,
 * } from "@packages/utilities/styled";
 *
 * type Direction = "row" | "column";
 *
 * type StackProps = {
 *   direction?: BreakpointPropFor<Direction>,
 * };
 *
 * const StackView = emotionStyled("div")(
 *   { display: "flex" },
 *   {
 *     variants: {
 *       direction: {
 *         column: {
 *           flexDirection: "column",
 *         },
 *         row: {
 *           flexDirection: "row",
 *         },
 *       },
 *     },
 *   }
 * );
 *
 * const Stack = (props: StackProps) => <StackView {...props} />
 * ```
 */

type Breakpoints = `@${keyof ThemeTokens['breakpoints']}` | '@initial';
export type BreakpointPropFor<PropT> =
  | PropT
  | RequireAtLeastOne<Partial<Record<Breakpoints, PropT>>, Breakpoints>;

const breakpointsKeys = Object.keys(tokens.breakpoints).map((bp) => `@${bp}`);

/**
 * Determines if the (potential) responsive prop is valid based on
 * our breakpoint system.
 */
const isResponsiveObjectVariant = (objectKeys: Array<string>): boolean => {
  return objectKeys.every((key) => breakpointsKeys.includes(key));
};

/**
 * Retrieves the responsive styles for props using the built-in
 * breakpoint system (above) for emotion styled.
 *
 * @example
 * ```
 * <Stack direction={{'@bp1': 'row', '@bp2': 'column}} />
 * ```
 */
export const getBreakpointVariantStylesFromProps = ({
  /**
   * The css function from our custom emotionStyled instance.
   */
  css = () => '',

  /**
   * Incoming props from a styled component.
   */
  props = {},

  /**
   * Variant styles from a styled component.
   */
  variants = {},

  /**
   * Default variant values from a styled component.
   */
  defaultVariants = {},
}: {
  css: (styles: any) => string;
  // Flow: These props and variants can literaly be anything.
  props: any;
  variants: any;
  defaultVariants: any;
}): Array<any | string> => {
  const classNames: Array<any | string> = [];

  // @ts-expect-error - TS7006 - Parameter 'styles' implicitly has an 'any' type.
  const compileStyles = (styles) => {
    if (!styles) return;
    const compiledStyles = css(styles);
    classNames.push(compiledStyles);
  };

  for (const key in props) {
    const value = props[key];
    const isVariant = variants[key];

    if (!isVariant) continue;
    /**
     * We're only processing objects
     */
    if (!value || typeof value !== 'object') continue;

    /**
     * We're plucking out @initial in case the user has specified an
     * initial variant style to render.
     */
    const {'@initial': initialVariant, ...breakpointValues} = value;
    const breakpointKeys = Object.keys(breakpointValues);
    const isResponsiveObject = isResponsiveObjectVariant(breakpointKeys);

    if (!isResponsiveObject) continue;

    const defaultVariant = defaultVariants[key];
    const initialVariantKey = initialVariant || defaultVariant;

    /**
     * We'll process the @initial variant first.
     */
    if (initialVariantKey) {
      const initialVariantStyles = variants[key][initialVariantKey];
      compileStyles(initialVariantStyles);
    }

    for (const breakpoint of breakpointKeys) {
      /**
       * Incoming breakpoint keys look like @bp2.
       * We need to normalize it: @bp2 -> bp2
       * This allows to correctly check against our breakpoint system values.
       */
      const bp = breakpoint.substr(1);
      const variant = value[breakpoint];

      /**
       * This value would be the style (object) defined in the variants key.
       *
       * @example
       * ```
       * {
       *  background: 'red',
       *  padding: 20
       * }
       * ```
       */
      const variantStyles = variants[key][variant];
      if (!variantStyles) continue;

      /**
       * Let's compile the styles (after all them checks!)
       */
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly bp1Only: `@media (max-width: ${number}px)`; readonly bp2: "@media (min-width: 576px)"; readonly bp3: "@media (min-width: 768px)"; readonly bp4: "@media (min-width: 992px)"; readonly bp5: "@media (min-width: 1200px)"; }'.
      const mediaQuery = tokens.breakpoints[bp];
      const stylesToCompile = {[mediaQuery]: variantStyles} as const;
      compileStyles(stylesToCompile);
    }
  }

  return classNames;
};
